import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Navigate, redirect, useRouter } from "@tanstack/react-router";
import { Container, LoaderCircle } from "lucide-react";
import { Suspense, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { AppHeader } from "~/components/layout/AppHeader";
import { HubMap } from "~/components/layout/HubMap/HubMap";
import { PageContainer } from "~/components/layout/PageContainer";
import { BackLink } from "~/components/one-delivery/BackLink";
import { DeliveryWeekdaysPicker } from "~/components/one-delivery/DeliveryWeekdays";
import { requireOnboarded } from "~/components/one-delivery/onboarding";
import type { settingsOneDeliveryHubSaveMutation } from "./__generated__/settingsOneDeliveryHubSaveMutation.graphql";
import type { settingsOneDeliveryQuery } from "./__generated__/settingsOneDeliveryQuery.graphql";

const query = graphql`
	query settingsOneDeliveryQuery {
		onedeliveryProfile {
			deliveryWeekdays
			preferredHub {
				id
				meta {
					id
					name
					street
					postalCode
					city
				}
			}
		}
	}
`;

const saveHubMutation = graphql`
	mutation settingsOneDeliveryHubSaveMutation($hubId: UUID!) {
		onedeliveryProfileSave(input: { preferredHubId: $hubId }) {
			userId
		}
	}
`;

export const Route = createFileRoute("/one-delivery/settings")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async ({ context }) => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/settings", show: "sign-in" } });
		}
		await requireOnboarded(context.relayEnvironment);
		return preloadQuery<settingsOneDeliveryQuery>(context.relayEnvironment, query, {}, { fetchPolicy: "network-only" });
	},
});

/** One Delivery settings: preferred Hub and preferred delivery weekdays. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const { onedeliveryProfile: profile } = usePreloadedQuery<settingsOneDeliveryQuery>(query, Route.useLoaderData());
	const [hubMapOpen, setHubMapOpen] = useState(false);
	const [saveHub, isSaving] = useAsyncMutation<settingsOneDeliveryHubSaveMutation>(saveHubMutation);

	const changeHub = async (hubId: string, close: () => void) => {
		try {
			await saveHub({ variables: { hubId } });
			close();
			await router.invalidate();
			toast.success(i18n.OD_SHIP_HUB_CHANGED());
		} catch (error) {
			toast.error(i18n.OD_ONB_HUB_SAVE_FAILED(), { description: error instanceof Error ? error.message : undefined });
		}
	};
	if (!profile) return <Navigate to="/one-delivery/onboarding/pricing" replace />;
	const hub = profile.preferredHub.meta;

	return (
		<PageContainer className="min-h-dvh bg-slate-50">
			<AppHeader />
			<BackLink to="/one-delivery" label={i18n.OD_BACK_TO_SHIPMENTS()} />
			<div className="flex flex-col gap-4 px-6 pt-4 pb-8">
				<h1 className="font-breit text-2xl font-normal text-neutral-900">
					<T>{i18n.OD_SETTINGS()}</T>
				</h1>

				<section className="flex flex-col gap-4 rounded-[14px] border border-neutral-200 bg-white p-6 shadow-sm">
					<p className="text-lg font-medium text-neutral-950">{i18n.OD_SETTINGS_HUB()}</p>
					<div className="flex gap-4">
						<Container className="size-6 shrink-0" />
						<div className="flex min-w-0 flex-col">
							<p className="font-breit text-base leading-none font-medium text-neutral-950">
								{i18n.OD_ONB_HUB_NAME({ name: hub?.name ?? "" })}
							</p>
							{hub && (
								<p className="pt-1 text-sm text-neutral-500">
									{hub.street}, {hub.postalCode} {hub.city}
								</p>
							)}
						</div>
					</div>
					<Button size="lg" className="w-full font-breit font-normal" onClick={() => setHubMapOpen(true)}>
						{i18n.OD_SETTINGS_CHANGE_HUB()}
					</Button>
				</section>

				<section className="rounded-[14px] border border-neutral-200 bg-white px-6 pt-6 pb-6 shadow-sm">
					<p className="text-lg font-medium text-neutral-950">{i18n.OD_WEEKDAYS_SELECTED()}</p>
					<p className="text-sm text-neutral-500">{i18n.OD_WEEKDAYS_HINT()}</p>
					<DeliveryWeekdaysPicker initial={profile.deliveryWeekdays} />
				</section>
			</div>
			{hubMapOpen && (
				<Suspense fallback={null}>
					<HubMap
						open={hubMapOpen}
						onOpenChange={setHubMapOpen}
						filterService="ONEDELIVERY"
						renderAction={({ id }, close) => (
							<Button className="w-full font-breit font-normal" disabled={isSaving} onClick={() => void changeHub(id, close)}>
								{isSaving && <LoaderCircle className="size-4 animate-spin" />}
								{i18n.CHOOSE_HUB()}
							</Button>
						)}
					/>
				</Suspense>
			)}
		</PageContainer>
	);
}
